import { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  FileText,
  MessageCircleMore,
  WandSparkles,
  MessageCirclePlus,
  MessageCircleX,
  Save
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useFeedForwardAIService from "@/services/feedforward-ai.service";
import { ConversationType } from "@/constants/chat.constant";
import ConfirmationDialog from "@/components/modal/confirmationModal";
import toast from "react-hot-toast";
import aiIcon from "@/assets/ai-logo.svg"

interface FeedForwardAIProps {
  onClose: () => void;
  conversationType?: ConversationType;
}

const FeedForwardAI = ({ onClose, conversationType = ConversationType.CHOP_GPT }: FeedForwardAIProps) => {
  const [isSystemPromptOpen, setIsSystemPromptOpen] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [rewriteGuidance, setRewriteGuidance] = useState("");
  const [userGuidance, setUserGuidance] = useState("");
  const [refinedPrompt, setRefinedPrompt] = useState("");
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(true);

  const { getSystemPrompt, analyzeAllFeedbacks, refinePrompt, savePrompt } = useFeedForwardAIService();

  useEffect(() => {
    let isMounted = true;
    
    const loadSystemPrompt = async () => {
      try {
        setIsLoadingPrompt(true);
        const response = await getSystemPrompt(conversationType);
        if (isMounted && response.data) {
          setSystemPrompt(response.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error loading system prompt:", error);
          toast.error("Failed to load system prompt");
        }
      } finally {
        if (isMounted) {
          setIsLoadingPrompt(false);
        }
      }
    };

    loadSystemPrompt();

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationType]); // Reload when conversation type changes

  const handleStartAnalyzing = async () => {
    setIsAnalyzing(true);
    try {
      const response = await analyzeAllFeedbacks({
        conversation_type: conversationType
      });
      if (response.data?.rewrite_guidance) {
        setRewriteGuidance(response.data.rewrite_guidance);
        toast.success(response.message || "All feedbacks analyzed successfully");
      }
    } catch (error: any) {
      console.error("Error analyzing feedbacks:", error);
      // Check if it's an auth error
      if (error?.status === 401 || error?.message?.includes("Authorization") || error?.message?.includes("Unauthorized")) {
        toast.error("Authentication failed. Please refresh the page and try again.");
      } else if (error?.status === 400) {
        toast.error("No valid feedbacks found to analyze")
      } else {
        const errorMessage = error?.detail || error?.message || "Failed to analyze feedbacks";
        toast.error(errorMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleRefinePrompt = async () => {
    if (!rewriteGuidance) {
      toast.error("Please analyze feedback first");
      return;
    }

    setIsRefining(true);
    try {
      const response = await refinePrompt({
        prompt_type: conversationType,
        ai_guidance: rewriteGuidance,
        user_guidance: userGuidance || undefined
      });

      if (response.data?.refined_prompt) {
        setRefinedPrompt(response.data.refined_prompt);
        setRewriteGuidance(response.data.refined_prompt);
        toast.success("Prompt refined successfully");
      }
    } catch (error: any) {
      console.error("Error refining prompt:", error);
      toast.error(error?.message || "Failed to refine prompt");
    } finally {
      setUserGuidance("");
      setIsRefining(false);
    }
  };

  const handleSaveChanges = () => {
    if (!rewriteGuidance) {
      toast.error("No changes to save");
      return;
    }
    setShowWarningModal(true);
  };

  const handleConfirmSave = async () => {
    setShowWarningModal(false);
    setIsSaving(true);
    try {
      const response = await savePrompt({
        prompt_type: conversationType,
        new_prompt: rewriteGuidance
      });

      if (response.data) {
        toast.success(`Prompt updated successfully. Version: ${response.data.version}`);
        try {
          const updatedPrompt = await getSystemPrompt(conversationType);
          if (updatedPrompt.data) {
            setSystemPrompt(updatedPrompt.data);
            setRewriteGuidance(updatedPrompt.data);
          }
        } catch (error) {
          console.error("Error reloading prompt after save:", error);
        }
      }
    } catch (error: any) {
      console.error("Error saving prompt:", error);
      toast.error(error?.message || "Failed to save prompt changes");
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  const handleReject = () => {
    setRewriteGuidance("");
    setUserGuidance("");
    setRefinedPrompt("");
  };

  return (
    <div className="flex flex-col h-full w-full text-text-500 bg-primary-admin_foreground">
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto space-y-4 mt-6">
        {/* System Prompt Section */}
        <div className="bg-white rounded-md border border-[#D3E2F8] overflow-hidden">
          <button
            onClick={() => setIsSystemPromptOpen(!isSystemPromptOpen)}
            className="w-full flex items-center justify-between px-4 hover:bg-gray-50 transition-colors"
          >
            <div className="w-full flex items-center justify-between gap-2 py-4">
              <MessageCircleMore size={18} className="text-primary-500" />
              <span className="text-base font-medium text-text-500 mr-auto">System Prompt</span>
              <span className="px-3 py-1 mr-3 text-xs font-medium rounded-full bg-primary-100 text-primary-700 border border-primary-300">{conversationType === ConversationType.CHOP_GPT ? "CHOP GPT" : "POLICY ASSISTANT"}</span>
            </div>
            {isSystemPromptOpen ? (
              <ChevronUp size={20} className="text-text-200" />
            ) : (
              <ChevronDown size={20} className="text-text-200" />
            )}
          </button>
          {isSystemPromptOpen && (
            <div className="px-4 pb-4 border-zinc-300">
              {isLoadingPrompt ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                </div>
              ) : (
                <textarea
                  value={systemPrompt}
                  readOnly
                  className="w-full h-[180px] p-2 border border-border font-secondary rounded-md text-xs resize-none focus:outline-none overflow-scroll"
                />
              )}
            </div>
          )}
        </div>

        {/* Analyze Feedback Section */}
        <div className="bg-white rounded-md border border-[#D3E2F8] px-4 pt-4">
          <div className="flex items-center">
            <FileText size={18} className="text-primary-500" />
            <div className="text-base font-medium text-text-500 pl-2">
              {rewriteGuidance ? "Reanalyze feedback" : "Analyze feedback"}
            </div>
          </div>
          <div className="flex items-start justify-between py-2">
            <div className="">
              <span className="text-sm py-2 font-secondary text-[#09090B] mb-2">
                {rewriteGuidance 
                  ? "Feedforward AI has analyzed your feedback and applied the insights to upgrade your system prompt. You can repeat the analysis whenever required."
                  : "Use Feedforward AI to analyze the feedback, extract insights, and produce an improved version of the system prompt."
                }
              </span>
              <Button
                onClick={handleStartAnalyzing}
                disabled={isAnalyzing}
                className="flex items-center bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 my-3"
              >
                {isAnalyzing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>Analyzing feedbacks...</span>
                  </>
                ) : (
                  <>
                    <WandSparkles size={16} />
                    <span>{rewriteGuidance ? "Reanalyze Feedback" : "Start analyzing"}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Rewrite Guidance and User Guidance Section */}
        {rewriteGuidance && <div className="space-y-1 border border-[#D3E2F8] rounded-md">
          {/* Rewrite Guidance Section */}
          <div className=" overflow-hidden px-4">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <img src={aiIcon} className="w-4 h-4"></img>
                <div className="text-base font-medium text-text-500">New System Prompt - by AI</div>
              </div>
              <button
                onClick={() => handleCopy(rewriteGuidance || "Analyze the feedback to gain guidance.")}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors text-[#5A5A63]"
                title="Copy"
              >
                <Copy size={16} className="text-text-200" />
              </button>
            </div>
            <div className="">
              <textarea
                value={rewriteGuidance}
                onChange={(e) => setRewriteGuidance(e.target.value)}
                placeholder="Analyze the feedback to gain guidance."
                readOnly
                className="w-full h-[180px] p-2 font-secondary border rounded-md text-[#5A5A63] text-xs resize-none focus:outline-none overflow-y-auto"
              />
            </div>
          </div>

          {/* User Guidance Section */}
          <div className="">
            <div className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-primary-500" />
                <div className="text-base font-medium text-text-500">User guidance (optional)</div>
              </div>
              <button
                onClick={() => handleCopy(userGuidance || "Add your guidance—what should happen next?")}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                title="Copy"
              >
                <Copy size={16} className="text-text-200" />
              </button>
            </div>
            <div className="px-4">
              <textarea
                value={userGuidance}
                disabled={!rewriteGuidance}
                onChange={(e) => setUserGuidance(e.target.value)}
                placeholder="Add your guidance—If you want something can be improved, please add here"
                className="w-full h-[120px] p-2 font-secondary border rounded-md text-[#5A5A63] text-sm resize-none focus:outline-none overflow-y-auto"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 px-4 pb-4">
            <Button
              onClick={handleRefinePrompt}
              disabled={isRefining || !rewriteGuidance || userGuidance.length <= 0}
              className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white"
            >
              {isRefining ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>Refining...</span>
                </>
              ) : (
                <>
                  <MessageCirclePlus size={16} />
                  <span>Refine Prompt</span>
                </>
              )}
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={isSaving || !rewriteGuidance}
              className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white ml-auto"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Save Changes</span>
                </>
              )}
            </Button>
            <Button
              onClick={handleReject}
              disabled={!rewriteGuidance}
              className="flex items-center justify-center gap-2 text-red-600 border border-red-600 p-1 px-4 rounded hover:bg-red-50"
            >
              <MessageCircleX size={16} />
              <span>Reject</span>
            </Button>
          </div>
        </div>}
      </div>

      {/* Warning Modal */}
      <ConfirmationDialog
        open={showWarningModal}
        title="Warning"
        description="This is a sensitive setting that will significantly alter the behavior of AI-generated responses. Proceed with caution."
        confirmText="I understand"
        cancelText="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setShowWarningModal(false)}
        variant="warning"
      />
    </div>
  );
};

export default FeedForwardAI;

